import { Button, Icon, InputV2 } from "@southfields-digital/mxmz-react-ui";
import classNames from "classnames";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

import type { StateType } from "src/redux/reducers";
import { getPlaylistMode } from "src/utils/playlist";

function MosBadge({ className }: { className?: string }) {
	return (
		<span className={classNames("font-mono text-xs font-bold text-foreground shrink-0", className)}>
			MOS
		</span>
	);
}

function PlaylistOptionLabel({ playlist }: { playlist: Playlist }) {
	const mode = getPlaylistMode(playlist);
	return (
		<div className="flex items-center justify-between gap-2 w-full">
			<span className="truncate">{playlist.name}</span>
			<span className="flex items-center gap-2">
				{mode === "mos" && <MosBadge className="text-gray-500" />}
				<span className="text-[10px] text-gray-500 shrink-0">{playlist.graphicCount} items</span>
			</span>
		</div>
	);
}

type PlaylistSelectorProps = {
	value: string | null;
	onChange: (playlistId: string) => void;
	onAdd: () => void;
};

const VIEWPORT_PADDING = 8;
const MIN_SCROLL_HEIGHT = 64;

export default function PlaylistSelector({ value, onChange, onAdd }: PlaylistSelectorProps) {
	const { data: playlists } = useSelector((state: StateType) => state.playlist.getAll);

	const selectedPlaylist = playlists?.find((p: Playlist) => p.id === value);

	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({
		position: "fixed",
		visibility: "hidden",
	});

	const triggerRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const close = useCallback(() => {
		setIsOpen(false);
		setQuery("");
		setPanelStyle({ position: "fixed", visibility: "hidden" });
	}, []);

	const applyPosition = useCallback(() => {
		const triggerEl = triggerRef.current;
		if (!triggerEl) return;

		const rect = triggerEl.getBoundingClientRect();
		const vh = window.visualViewport?.height ?? window.innerHeight;
		const ph = panelRef.current?.offsetHeight ?? 0;

		const top = rect.bottom;
		const spaceBelow = vh - VIEWPORT_PADDING - top;
		const needsClamp = top + ph > vh - VIEWPORT_PADDING;

		const style: React.CSSProperties = {
			position: "fixed",
			visibility: "visible",
			top,
			left: rect.left,
			width: rect.width,
			zIndex: 9999,
		};

		if (needsClamp) {
			style.maxHeight = Math.max(MIN_SCROLL_HEIGHT, spaceBelow);
			style.overflowY = "auto";
		}

		setPanelStyle(style);
	}, []);

	// Position the panel when open
	useLayoutEffect(() => {
		if (!isOpen) return;
		applyPosition();

		const panelEl = panelRef.current;
		if (!panelEl || typeof ResizeObserver === "undefined") return;

		const ro = new ResizeObserver(() => applyPosition());
		ro.observe(panelEl);
		return () => ro.disconnect();
	}, [isOpen, applyPosition, query]);

	// Reposition on scroll/resize
	useEffect(() => {
		if (!isOpen) return;

		window.addEventListener("scroll", applyPosition, true);
		window.addEventListener("resize", applyPosition);
		return () => {
			window.removeEventListener("scroll", applyPosition, true);
			window.removeEventListener("resize", applyPosition);
		};
	}, [isOpen, applyPosition]);

	// Close on outside click
	useEffect(() => {
		if (!isOpen) return;

		const handleClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
				close();
			}
		};

		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [isOpen, close]);

	// Close on Escape
	useEffect(() => {
		if (!isOpen) return;

		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};

		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [isOpen, close]);

	const handleOpen = () => {
		if (!isOpen) {
			setIsOpen(true);
			requestAnimationFrame(() => inputRef.current?.focus());
		}
	};

	const handleSelect = (playlistId: string) => {
		onChange(playlistId);
		close();
	};

	const filteredPlaylists =
		playlists?.filter((p: Playlist) => {
			if (!query) return true;
			return p.name.toLowerCase().includes(query.toLowerCase());
		}) ?? [];

	const panel = isOpen
		? ReactDOM.createPortal(
				<div
					ref={panelRef}
					style={panelStyle}
					className="rounded-b bg-[#191a26] border border-t-0 border-[rgba(249,250,251,0.15)] shadow-[0px_0px_12px_rgba(0,0,0,0.4)] [clip-path:inset(0px_-12px_-12px_-12px)] overflow-x-hidden"
				>
					<ul className="flex flex-col pb-1">
						{filteredPlaylists.map((playlist: Playlist) => (
							<li
								key={playlist.id}
								className="flex items-center gap-2 px-3 h-7 text-white cursor-pointer transition-colors duration-100 hover:bg-[#323342]"
								onClick={() => handleSelect(playlist.id)}
							>
								<span className="flex-1 text-sm font-medium">
									<PlaylistOptionLabel playlist={playlist} />
								</span>
							</li>
						))}
						{filteredPlaylists.length === 0 && (
							<li className="px-3 h-7 flex items-center text-sm text-gray-500">
								No playlists found
							</li>
						)}
					</ul>
				</div>,
				document.body,
			)
		: null;

	return (
		<div className="flex items-center gap-2 shrink-0">
			<div ref={triggerRef} className="flex-1" onClick={handleOpen}>
				<InputV2
					ref={inputRef}
					type="text"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={
						selectedPlaylist?.name !== undefined && !isOpen
							? selectedPlaylist.name
							: "Select playlist"
					}
					className={classNames(
						selectedPlaylist?.name !== undefined && !isOpen && "!placeholder-white",
					)}
					wrapperClassName={classNames(isOpen && "!rounded-b-none")}
					suffix={
						<>
							{getPlaylistMode(selectedPlaylist) === "mos" && <MosBadge />}
							<Icon icon="list-magnifying-glass" weight="bold" />
						</>
					}
					onClick={handleOpen}
					onKeyDown={(e) => {
						if (e.key === "Escape") close();
					}}
				/>
			</div>
			{panel}
			{onAdd && (
				<Button
					iconProps={{ icon: "plus", weight: "bold" }}
					onClick={onAdd}
					title="Add playlist"
					color="darkGray"
					buttonSize="medium"
				/>
			)}
		</div>
	);
}
